import { Button, InfoBadge, InfoBar, InfoPage } from "@shapr3d/frontend-lib";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { useHandleLoginRedirect } from "../../containers/AppAuthContainer";
import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import { useResendVerificationEmail } from "../../hooks/useAuth";
import { useIsSharedWithMeEnabled, useRequestProjectAccess } from "../../hooks/useProjectSharing";
import { useTracker } from "../../hooks/useTracker";

import style from "./RequestAccessPage.module.css";

export default function RequestAccessPage({ isPendingRequest }: { isPendingRequest?: boolean }) {
  const isSharedWithMeEnabled = useIsSharedWithMeEnabled();

  const { data: accountData } = useAccount();
  const { projectId } = useParams();
  const tracker = useTracker();

  const onLoginRedirect = useHandleLoginRedirect();
  const handleLoginRedirect = () => {
    tracker.trackProjectPageAccessSwitchUser({
      projectId: projectId || "",
    });
    onLoginRedirect();
  };
  const {
    mutate: mutateResendVerificationEmail,
    isPending: isResendVerificationEmailPending,
    isSuccess: isResendVerificationEmailSuccessful,
  } = useResendVerificationEmail();

  const queryClient = useQueryClient();

  const {
    mutateAsync: requestProjectAccessMutate,
    isPending: isRequestAccessPending,
    isError: isRequestAccessError,
    isSuccess: isRequestAccessSuccessful,
  } = useRequestProjectAccess({});

  const handleRequestAccess = () => {
    tracker.trackProjectPageAccessRequested({
      projectId: projectId || "",
    });
    requestProjectAccessMutate();
    queryClient.invalidateQueries({ queryKey: ["project"] });
  };

  const { goToProfile } = useNavigation();

  if (isPendingRequest || isRequestAccessSuccessful) {
    return (
      <InfoPage
        variant="success"
        title={`Access Requested`}
        description={
          <div
            className={style.message}
          >{`You can access the project once your request is approved.`}</div>
        }
        primaryCTA={
          <Button
            variant="grayscale"
            onClick={() => {
              tracker.trackGoToDashboard({
                location: "request_access_page",
                destination: isSharedWithMeEnabled ? "shared_with_me" : "review_links",
              });
              goToProfile();
            }}
          >
            Go to Dashboard
          </Button>
        }
      />
    );
  }

  return (
    <InfoPage
      variant="permission"
      title={
        !accountData?.emailVerified ? `Email Address Not Verified` : `Request Access to Project`
      }
      description={
        <>
          <div className={style.message}>
            {!accountData?.emailVerified
              ? `To access this project, we'll send you a verification email. Once you've verified, just come back to this page and refresh to continue.`
              : `You can access the project once your request is approved.`}
          </div>
          <InfoBadge
            superTitle="You're currently logged in as:"
            title={accountData?.name}
            description={accountData?.email}
            cta={
              <Button variant="grayscale" onClick={handleLoginRedirect}>
                Switch User Profile
              </Button>
            }
          />
        </>
      }
      primaryCTALabel={
        (isResendVerificationEmailSuccessful && (
          <InfoBar
            variant="success"
            title="Verification Email Sent"
            text="Check your email for the verification link, then refresh this page to continue."
            className={style.successInfoBar}
          />
        )) ||
        (isRequestAccessError && (
          <InfoBar
            variant="critical"
            title="Something Went Wrong"
            text="We couldn't process your request. Please try again later."
          />
        ))
      }
      primaryCTA={
        <>
          {!accountData?.emailVerified && !isResendVerificationEmailSuccessful ? (
            <Button
              variant="primary"
              disabled={isResendVerificationEmailPending}
              onClick={() => {
                mutateResendVerificationEmail();
              }}
            >
              Verify Email to Continue
            </Button>
          ) : (
            !isResendVerificationEmailSuccessful && (
              <Button
                variant="primary"
                onClick={handleRequestAccess}
                disabled={isRequestAccessPending}
              >
                Request Access
              </Button>
            )
          )}
        </>
      }
    />
  );
}
